import { useRef, useState, type FC } from "react";
import { ComponentBaseContainer } from "./TextDiff.style";
import SideNav from "../nav/SideNav";
import { ComponentContainer, JsonContainer, JsonHelperContainer, JsonTextContainer, StyledClearIcon, StyledCopyIcon } from "../jsonformatter/JsonFormatter.style";
import { diffWordsWithSpace } from "diff";
import ace from "ace-builds/src-noconflict/ace";
import AdsComponent from "../AdsComponent";
import Divider from "@mui/material/Divider";
import { Helmet } from "react-helmet-async";

const TextDiff: FC = () => {
    const editorARef = useRef<any>(null);
    const editorBRef = useRef<any>(null);
    const Range = ace.require("ace/range").Range;

    const [showToast, setShowToast] = useState(false);
    function handleInput(): void {
        const orignal = editorARef.current?.editor?.getValue();
        const changed = editorBRef.current?.editor?.getValue();
        if (orignal && changed) {
            const editorA = editorARef.current.editor;
            const editorB = editorBRef.current.editor;
            const sessionA = editorA.getSession();
            const sessionB = editorB.getSession();
            const clearMarkers = (session: any) => {
                const markers = session.getMarkers(false);
                if (markers) {
                    Object.keys(markers).forEach((key) => session.removeMarker(Number(key)));
                }
            };
            clearMarkers(sessionA);
            clearMarkers(sessionB);
            const diffs = diffWordsWithSpace(orignal, changed);
            let aOffset = 0;
            let bOffset = 0;

            diffs.forEach((part) => {
                const value = part.value;
                const length = value.length;

                if (part.removed) {
                    const start = sessionA.doc.indexToPosition(aOffset, 0);
                    const end = sessionA.doc.indexToPosition(aOffset + length, 0);
                    sessionA.addMarker(new Range(start.row, start.column, end.row, end.column), 'diff-add', 'text');
                    aOffset += length;
                } else if (part.added) {
                    const start = sessionB.doc.indexToPosition(bOffset, 0);
                    const end = sessionB.doc.indexToPosition(bOffset + length, 0);
                    sessionB.addMarker(new Range(start.row, start.column, end.row, end.column), 'diff-remove', 'text');
                    bOffset += length;
                } else {
                    aOffset += length;
                    bOffset += length;
                }
            });

        }
    }

    function clearEditor(ref: React.RefObject<any>): void {
        ref.current?.editor?.setValue("", -1);
    }

    const handleCopy = (ref: React.RefObject<any>) => {
        const content = ref.current?.editor?.getValue();
        content && navigator.clipboard.writeText(content)
            .then(() => {
                setShowToast(true);
                setTimeout(() => setShowToast(false), 1000);
            })
    }



    return (
        <ComponentBaseContainer>
            <Helmet>
                <title>Text Difference Checker | Compare Text Side-by-Side</title>
                <meta name="description" content="Compare two text files side-by-side and highlight differences. Fast, accurate, and easy-to-use diff checker." />
                <meta name="keywords" content="text diff, compare text, diff checker, text comparison tool, online diff" />
            </Helmet>

            <SideNav />
            <ComponentContainer>
                <JsonContainer>
                    <JsonHelperContainer>
                        <StyledCopyIcon onClick={() => handleCopy(editorARef)} />
                        <StyledClearIcon onClick={() => clearEditor(editorARef)} />
                    </JsonHelperContainer>
                    <JsonTextContainer
                        ref={editorARef}
                        placeholder="Type (or) paste here..."
                        mode="text"
                        name="text-editor"
                        wrapEnabled
                        onInput={handleInput}
                        setOptions={{
                            useWorker: false,
                            showLineNumbers: true,
                            tabSize: 2,
                        }}
                    />
                </JsonContainer>
                <Divider orientation="vertical" />
                <JsonContainer>
                    <JsonHelperContainer>
                        <StyledCopyIcon onClick={() => handleCopy(editorBRef)} />
                        <StyledClearIcon onClick={() => clearEditor(editorBRef)} />
                    </JsonHelperContainer>
                    <JsonTextContainer
                        ref={editorBRef}
                        placeholder="Type (or) paste to compare..."
                        mode="text"
                        name="text-editor"
                        wrapEnabled
                        onInput={handleInput}
                        setOptions={{
                            useWorker: false,
                            showLineNumbers: true,
                            tabSize: 2,
                        }}
                    />
                </JsonContainer>
            </ComponentContainer>
            <AdsComponent />
            {showToast && (
                <div style={{
                    position: "absolute",
                    top: "10px",
                    left: "50%",
                    transform: "translateX(-50%)",
                    background: "#323232",
                    color: "white",
                    padding: "8px 16px",
                    borderRadius: "6px",
                    opacity: 0.9,
                    fontSize: "14px",
                }}>
                    Copied to clipboard!
                </div>
            )}
        </ComponentBaseContainer>
    );
}

export default TextDiff;